import type { MutableRefObject } from 'react';
import { createRef, useCallback } from 'react';

export type RegisterElementRef = (
    id: string,
) => (element: HTMLElement | null) => MutableRefObject<HTMLElement | null>;

export type GetElementRef = (id: string) => MutableRefObject<HTMLElement | null>;

export type RegisterElementExistingRef = (
    id: string,
    ref: MutableRefObject<HTMLElement | null>,
) => void;

export type UseRefTracker = {
    registerElementRef: RegisterElementRef;
    getElementRef: GetElementRef;
    registerElementExistingRef: RegisterElementExistingRef;
};

export const useRefTracker = (
    elementRefs: MutableRefObject<Map<string, MutableRefObject<HTMLElement | null>>>,
): UseRefTracker => {
    const registerElementRef = useCallback(
        (id: string) =>
            (element: HTMLElement | null): MutableRefObject<HTMLElement | null> => {
                const map = elementRefs?.current;
                if (map.has(id) && element) {
                    const ref = map.get(id) ?? { current: null };
                    ref.current = element;
                    return ref;
                }
                if (!map.has(id) && element) {
                    const newRef = createRef<HTMLElement | null>();
                    map.set(id, newRef);
                    if (element) {
                        // @ts-expect-error - createRef doesn't return a MutableRefObject
                        newRef.current = element;
                    }
                    return newRef;
                }

                return { current: null };
            },
        [elementRefs],
    );
    const getElementRef = useCallback(
        (id: string) => elementRefs.current?.get(id) ?? { current: null },
        [elementRefs],
    );

    const registerElementExistingRef = useCallback(
        (id: string, ref: MutableRefObject<HTMLElement | null>) => {
            const map = elementRefs?.current;
            if (!map.has(id)) {
                map.set(id, ref);
            }
        },
        [elementRefs],
    );

    return {
        getElementRef,
        registerElementRef,
        registerElementExistingRef,
    };
};
